import { createSlice } from '@reduxjs/toolkit'
import { gameClose, userLogin } from 'redux-store/thunk/auth.thunk'
import { fetchUserInformation } from 'redux-store/thunk/user.thunk'
import { getPayloadFromToken } from 'utils/common-functions/index'
import { plus, minus } from 'number-precision'
import { CURRENCY_SYMBOLS } from 'utils/constants/index'

const defaultAuthState = {
  creditWalletWaitQueue: [],
  creditDemoWalletWaitQueue: [],
  userInfo: null,
  userDetails: null,
  currencyId: null,
  currencyCode: null,
  isLoggedIn: false,
  showName: false,
  demoMode: false,
  demoWallet: { balance: 3000, currency: 'EUR' }
}

const authSlice = createSlice({
  name: 'authSlice',
  initialState: defaultAuthState,
  reducers: {
    updateUserWallet: (state, action) => ({
      ...state,
      userInfo: {
        ...state.userInfo,
        wallet: action.payload
      }
    }),
    debitBalance: (state, { payload }) => {
      const debitAmt = Number(payload)
      const _userInfoWallet = { ...state.userInfo.wallet }
      if (debitAmt) {
        _userInfoWallet.balance = minus(_userInfoWallet.balance, debitAmt)
      }
      return ({
        ...state,
        userInfo: {
          ...state.userInfo,
          wallet: _userInfoWallet
        }
      })
    },
    creditBalance: (state, { payload }) => {
      const creditAmt = Number(payload)
      const _userInfoWallet = { ...state.userInfo.wallet }
      if (creditAmt) {
        _userInfoWallet.balance = plus(_userInfoWallet.balance, creditAmt)
      }
      return ({
        ...state,
        userInfo: {
          ...state.userInfo,
          wallet: _userInfoWallet
        }
      })
    },
    enqueueCreditWalletWaitQueue: (state, action) => {
      return {
        ...state,
        creditWalletWaitQueue: [...state.creditWalletWaitQueue, action.payload]
      }
    },
    dequeueCreditWalletWaitQueue: (state) => {
      let _creditWalletWaitQueue = [...state.creditWalletWaitQueue]
      const creditAmt = Number(_creditWalletWaitQueue?.[0])
      const _userInfoWallet = { ...state.userInfo.wallet }
      if (typeof creditAmt === 'number' && creditAmt >= 0) {
        _userInfoWallet.balance = plus(_userInfoWallet.balance, creditAmt)
        _creditWalletWaitQueue = _creditWalletWaitQueue?.slice(1)
      }

      return {
        ...state,
        creditWalletWaitQueue: _creditWalletWaitQueue,
        userInfo: {
          ...state.userInfo,
          wallet: _userInfoWallet
        }
      }
    },
    updateCreditDemoWalletWaitQueue: (state, action) => {
      let _creditDemoWalletWaitQueue = [...state.creditDemoWalletWaitQueue]
      const _newDemoWallet = _creditDemoWalletWaitQueue.pop() ?? state.demoWallet
      _creditDemoWalletWaitQueue = _creditDemoWalletWaitQueue?.slice(0, _creditDemoWalletWaitQueue.length - 1)
      return {
        ...state,
        creditDemoWalletWaitQueue: _creditDemoWalletWaitQueue,
        demoWallet: _newDemoWallet
      }
    },
    appendCreditDemoWalletWaitQueue: (state, action) => {
      return {
        ...state,
        creditDemoWalletWaitQueue: [action.payload, ...state.creditDemoWalletWaitQueue]
      }
    },
    setDemoMode: (state, action) => {
      return {
        ...state,
        demoMode: action.payload,
        currencyCode: state.demoWallet.currency
      }
    },
    setDemoWalletCurrency: (state, action) => ({
      ...state,
      currencyCode: action.payload.currencyCode,
      currencyId: action.payload?.currencyId,
      currencySymbol: CURRENCY_SYMBOLS?.[action.payload.currencyCode] || '$',
      demoWallet: { ...state.demoWallet, currency: action.payload.currencyCode },
      operatorId: action.payload?.operatorId
    }),
    updateDemoWalletBalance: (state, action) => {
      return {
        ...state,
        demoWallet: { ...state.demoWallet, balance: action.payload }
      }
    },
    updateDemoWalletBalanceForBetPlace: (state, action) => {
      const newBalance = (action.payload.operation === 'add') ? state.demoWallet.balance + Number(action.payload.amount) : state.demoWallet.balance - Number(action.payload.amount)

      return {
        ...state,
        demoWallet: { ...state.demoWallet, balance: newBalance }
      }
    },
    setShowName: (state, action) => {
      return {
        ...state,
        showName: !state.showName
      }
    },
    setUserInfo: (state, action) => ({
      ...state,
      userInfo: {
        ...state.userInfo,
        wallet: action.payload
      }
    })
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogin.fulfilled, (state, action) => {
        const { user, wallet, accessToken } = action.payload
        const payload = getPayloadFromToken(accessToken)
        const currencyId = payload?.currencyId
        const currencyCode = payload?.currencyCode
        return {
          ...state,
          currencyId,
          currencyCode,
          userInfo: { user, wallet },
          isLoggedIn: true
        }
      })
      .addCase(fetchUserInformation.fulfilled, (state, action) => {
        return {
          ...state,
          userDetails: action.payload
        }
      })
      .addCase(gameClose.fulfilled, (state, action) => {
        return defaultAuthState
      })
  }
})

export const {
  setUserInfo, updateUserWallet, enqueueCreditWalletWaitQueue,
  dequeueCreditWalletWaitQueue, setShowName, appendCreditDemoWalletWaitQueue, updateCreditDemoWalletWaitQueue, debitBalance, creditBalance, setDemoMode, updateDemoWalletBalance, updateDemoWalletBalanceForBetPlace, setDemoWalletCurrency
} = authSlice.actions

export default authSlice.reducer
